import { useState, createContext, useEffect } from "react";

import { useAuth } from "./useAuth";
import { useApi } from "./useApi";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { getMonthRange, getQualificationPeriodMonth, isQualificationPeriod } from "../common/date-functions";
import { getSponsorUnicityId, getCustomerCategory } from "../common/helper-functions";

const PartnersContext = createContext([{}, () => { }]);

// office-api's `period` ("YYYYMM") as a month dropdown value ("YYYY-MM").
const toMonthValue = (period) =>
  /^\d{6}$/.test(period ?? "") ? `${period.slice(0, 4)}-${period.slice(4)}` : null;

// The dropdown value of the month before `monthValue` ("YYYY-MM").
const previousMonthValue = (monthValue) => {
  const [year, month] = monthValue.split("-").map(Number);
  return month === 1
    ? `${year - 1}-12`
    : `${year}-${String(month - 1).padStart(2, "0")}`;
};

// Marks a list request that was dropped for the month before it.
const MONTH_FALLBACK = Symbol("month fallback");

const PartnersContextProvider = (props) => {
  const { userInfo } = useAuth();
  const { sendRequest } = useApi();

  // use viewport to determine default pagesize
  const theme = useTheme();
  const viewportIsSmall = useMediaQuery(theme.breakpoints.up("sm"));

  const [downlineUid, setDownlineUid] = useState(userInfo.unicityId);
  const [downlineUidInfo, setDownlineUidInfo] = useState({});
  const [contextUserJoinDate, setContextUserJoinDate] = useState("");
  const [contextUserFirstName, setContextUserFirstName] = useState("Frontline");
  const [contextUserId, setContextUserId] = useState("");
  const [contextUserStats, setContextUserStats] = useState(null);
  const [contextUserType, setContextUserType] = useState("");
  // GO-3345: the unicity id of the sponsor of whoever's page is being viewed.
  // Restored (it was removed as dead in b43c4e1a, when the type-matching
  // redesign made the old frontline-anchored depth cap obsolete) because row
  // clickability now needs to know how DEEP the page owner is, not just their
  // type: member rows on a member's page are level 2 -- and clickable -- only
  // if that page owner is one of the user's own frontline members.
  const [contextUserSponsorUnicityId, setContextUserSponsorUnicityId] = useState(undefined);
  const [customerNotes, setCustomerNotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [includeMembers, setIncludeMembers] = useState(false);
  const [isLevelLimitMax, setIsLevelLimitMax] = useState(false);
  const [gridUpdateTimestamp, setGridUpdateTimestamp] = useState()
  const [hasError, setHasError] = useState(false);
  const [leg1UnicityId, setLeg1UnicityId] = useState(null);
  const [leg2UnicityId, setLeg2UnicityId] = useState(null);

  // DataGrid state
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  // store sort model, column visibility model, and mobile column in local storage to persist state across refereshes and sessions
  const [sortModel, setSortModel] = useState(
    sessionStorage.getItem("partnersSortModel")
      ? JSON.parse(sessionStorage.getItem("partnersSortModel"))
      : [{ field: "totalOv", sort: "desc" }]
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    sessionStorage.getItem("partnersColumnVisibilityModel")
      ? JSON.parse(sessionStorage.getItem("partnersColumnVisibilityModel"))
      : viewportIsSmall ? {
        name: true,
        nextRank: true,
        newPartners: true,
        centurionCount: true,
        pv: true,
        uncompressedTv: true,
        totalOv: true,
        relevance: false,
      } : {
        name: true,
        nextRank: false,
        newPartners: false,
        centurionCount: false,
        pv: false,
        uncompressedTv: false,
        totalOv: true,
        relevance: false,
      }
  );

  // activity filter button state
  const [filterSelection, setFilterSelection] = useState("all-statuses");
  const [filterValue, setFilterValue] = useState("all-statuses");

  // month dropdown
  const [contextMonthRange, setContextMonthRange] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");

  const resetContext = () => {
    setDownlineUid("");
    setDownlineUidInfo({});
    setContextUserJoinDate("");
    setContextUserFirstName("Frontline");
    setContextUserSponsorUnicityId(undefined);
    setContextUserType("");
    setCustomerNotes(false);
    setLoading(false);
    setRows([]);
    setRowCount(0);
    setPage(0);
    setPageSize(50);
    setFilterSelection("all-statuses");
    setFilterValue("all-statuses");
    setSearchQuery("");
  };

  const setUpdateTimestamp = (membersTimestamp, partnersTimestamp) => {
    if (new Date(membersTimestamp) < new Date(partnersTimestamp)) {
      setGridUpdateTimestamp(membersTimestamp)
    } else {
      setGridUpdateTimestamp(partnersTimestamp)
    }
  }

  useEffect(() => {
    if (!userInfo.loggedIn) {
      resetContext();
    }
  }, [userInfo]);

  // on mount get and set default month
  useEffect(() => {
    // get month range and set default value to most recent month
    setContextMonthRange(getMonthRange(3));
    setSelectedMonth(getQualificationPeriodMonth(3));
  }, []);

  // clear DG state when downlineUid changes
  useEffect(() => {
    setRows([]);
    setPage(0);
    setRowCount(0);
  }, [downlineUid]);

  let hasErroredOnce = false;

  // hit API when parameters change or are set
  useEffect(() => {
    const abortController = new AbortController();

    const { loggedIn, unicityId } = userInfo;

    const handleError = (err) => {
      setHasError(true);
      console.log(err);

      //if we hit an error with the current month, maybe the commission engine hasn't run yet;
      //try getting the previous month's data.
      if (!hasErroredOnce) {
        const month = getMonthRange(2)[1];
        setSelectedMonth(month.value);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
      hasErroredOnce = true;
    };

    // GO-3549: a month that hasn't synced yet has no partner list, and
    // office-api answers 500 for it (until office.backend #790 serves the
    // month before instead). In days 1-4, for the current month or a later
    // one, drop back a month rather than show the error screen. Any other
    // 5xx, a past month, or a fallback month that fails too still errors, so
    // a real failure isn't hidden and this can't loop. Once #790 is live this
    // only catches non-rollover errors; revisit it then.
    const handleListError = (err) => {
      if (
        err?.response?.status >= 500 &&
        isQualificationPeriod() &&
        selectedMonth >= getMonthRange(1)[0].value
      ) {
        console.log(err);
        setSelectedMonth(previousMonthValue(selectedMonth));
        return MONTH_FALLBACK;
      }
      return handleError(err);
    };

    if (loggedIn && downlineUid && unicityId) {
      setLoading(true);
      setRows([]);
      setContextUserFirstName("");
      // clear synchronously so a stale sponsor/type value from the previous
      // distributorId can never be read as "current" while the new
      // distributor's own record is still in flight -- a stale sponsor here
      // would make rows clickable on the wrong page
      setContextUserSponsorUnicityId(undefined);
      setContextUserType("");
      const offset = page * pageSize;

      const filterQueryParam =
        filterValue !== "all-statuses" ? `&customerStatus=${filterValue}` : "";
      const searchQueryParam = searchQuery ? `&searchTerm=${searchQuery}` : "";
      const promiseArray = [];
      const includeMembersQuery = includeMembers ? "&type=all" : "&type=partners";

      const promise1 = sendRequest({
        method: "get",
        endpoint: `${process.env.REACT_APP_OFFICE_API
          }/${downlineUid}/partnersmembers?sortField=${sortModel[0].field
          }&sortDirection=${sortModel[0].sort
          }&offset=${offset}&limit=${pageSize}${filterQueryParam}${searchQueryParam}${includeMembersQuery}&period=${selectedMonth.replace(
            "-",
            ""
          )}&distributorUnicityId=${unicityId}`,
        addPrefix: false,
        abortController,
      }).catch(handleListError);

      promiseArray.push(promise1);

       
      if (downlineUid != userInfo.unicityId) {
        const promise2 = sendRequest({
          method: "get",
          endpoint: `${process.env.API_URL}/customers?unicity=${downlineUid}&expand=customer`,
          addPrefix: false,
          abortController,
        }).catch(handleError);

        const promise3 = sendRequest({
          method: "get",
          endpoint: `${process.env.REACT_APP_OFFICE_API}/${downlineUid}/stats`,
          addPrefix: false,
          abortController,
        }).catch((err) => console.log(err));

        promiseArray.push(promise2);
        promiseArray.push(promise3);

        const promise4 = sendRequest({
          method: "get",
          addPrefix: false,
          endpoint: `${process.env.REACT_APP_OFFICE_API}/${userInfo.unicityId}/notes?customerId=${downlineUid}`,
          abortController,
        }).catch((err) => console.log(err));

        promiseArray.push(promise4);

        // GO-2724 / GO-3345: confirmed against live QA data that
        // customers?unicity=X&expand=customer (promise2, used above for .type)
        // never returns sponsor data at all, regardless of field path. Only the
        // href-based lookup does -- the same endpoint MemberContext.js chains
        // for customerHrefData. Chained off promise2's own resolution to avoid a
        // second unicity lookup, and appended at the END of promiseArray (not
        // inserted after promise2) so none of the existing values[] indices
        // above have to shift.
        const promise5 = promise2.then((res) => {
          const href = res?.data?.items?.[0]?.href;
          if (!href) return undefined;
          return sendRequest({
            method: "get",
            addPrefix: false,
            endpoint: `${href}?expand=profilePicture`,
            abortController,
          });
        }).catch((err) => {
          console.log(err);
          return undefined;
        });

        promiseArray.push(promise5);
      }

      Promise.all(promiseArray)
        .then((values) => {
          // The month before is being fetched instead; let that fill the grid.
          if (values[0] === MONTH_FALLBACK) return;

          // GO-3549: until a new month has synced, office-api serves the
          // month before it and names it in `period` (GO-3548). Move the
          // dropdown there instead of showing those numbers under the month
          // that was asked for; the fetch for the served month fills the grid.
          // Only ever earlier, so this can't loop.
          const servedMonth = toMonthValue(values[0]?.data?.period);
          if (servedMonth && servedMonth < selectedMonth) {
            setSelectedMonth(servedMonth);
            return;
          }

          setRowCount(values[0]?.data?.hits || 0);
          setIsLevelLimitMax(values[0]?.data?.isDistributorLevelLimit || false)
          setUpdateTimestamp(values[0]?.data?.members_last_updated, values[0]?.data?.partners_last_updated)
          setLeg1UnicityId(values[0]?.data?.leg1UnicityId)
          setLeg2UnicityId(values[0]?.data?.leg2UnicityId)
          setRows(
            values[0]?.data?.items?.map((item, idx) => {
              item.id = idx;
              return item;
            })
          );

          // values[4] = promise5 (href-based lookup) -- sponsor data isn't on
          // values[1] (see promise5 above)
          setContextUserSponsorUnicityId(getSponsorUnicityId(values[4]?.data));

          if (values[1] && values[1].data?.items[0]?.type) {
            setContextUserType(getCustomerCategory(values[1].data?.items[0]?.type));
          }

          if (values[1] && values[1].data?.items[0].humanName.fullName)
            setContextUserFirstName(
              `${values[1].data?.items[0].humanName.firstName ||
              values[1].data?.items[0].humanName.fullName
              }`
            );
          else setContextUserFirstName("");

          if (values[1] && values[1].data?.items[0].unicity)
            setContextUserId(values[1].data?.items[0].unicity);
          else setContextUserId("");

          // set user stats if third promise value exists
          if (values[2]) setContextUserStats(values[2].data);

          setCustomerNotes(values[3] !== undefined ? values[3].data.items : [])

          setLoading(false);

          if (values[1]?.data) {
            setContextUserJoinDate(values[1]?.data?.items[0]?.joinDate);
          }
        })
        .catch((err) => {
          setLoading(false);
          setHasError(true);
          console.log(err);
        });
    }

    return () => abortController.abort();
     
  }, [
    page,
    pageSize,
    sortModel,
    filterValue,
    downlineUid,
    selectedMonth,
    userInfo,
    searchQuery,
    includeMembers
  ]);

  useEffect(() => {
    if (!viewportIsSmall) {
      setPageSize(10);
    }
  }, [viewportIsSmall]);

  return (
    <PartnersContext.Provider
      value={{
        rows,
        filterSelection,
        setFilterSelection,
        filterValue,
        setFilterValue,
        contextMonthRange,
        selectedMonth,
        setSelectedMonth,
        rowCount,
        setRowCount,
        page,
        setPage,
        pageSize,
        setPageSize,
        columnVisibilityModel,
        setColumnVisibilityModel,
        sortModel,
        setSortModel,
        loading,
        setLoading,
        downlineUid,
        setDownlineUid,
        downlineUidInfo,
        setDownlineUidInfo,
        contextUserFirstName,
        contextUserId,
        contextUserJoinDate,
        contextUserStats,
        customerNotes,
        resetContext,
        searchQuery,
        setSearchQuery,
        includeMembers,
        setIncludeMembers,
        contextUserType,
        contextUserSponsorUnicityId,
        isLevelLimitMax,
        gridUpdateTimestamp,
        hasError,
        leg1UnicityId,
        leg2UnicityId,
      }}
    >
      {props.children}
    </PartnersContext.Provider>
  );
};

export { PartnersContext, PartnersContextProvider };
